<template>
    <span class="ziffeoms" :class="{ disabled, checked }">
        <input
            v-bind="$attrs"
            ref="input"
            type="checkbox"
            autocomplete="off"
            :disabled="disabled"
            v-model="checked"
            @keydown.enter="toggle"
        />
        <span
            ref="button"
            role="checkbox"
            class="button"
            :aria-checked="checked"
            @click.prevent="toggle"
        >
            <mg-icon icon="check" class="check" />
        </span>
    </span>
</template>

<script lang="ts" setup>
import { useTemplateRef } from "vue";
import Ripple from "@/components/MkRipple.vue";
import { refProp } from "@/composables/reactive";
import { useSettingsStore } from "@/store/settings-store";
import { storeToRefs } from "pinia";

import { spawnPopover } from "@/components/layout/popover-layer";

const props = defineProps<{
    disabled?: boolean;
}>();

const checked = defineModel<boolean>("checked", {
    required: true,
});

const settingsStore = useSettingsStore();
const { settings } = storeToRefs(settingsStore);
const animation = refProp(settings, "animation");

const button = useTemplateRef("button");

const toggle = () => {
    if (props.disabled) return;
    checked.value = !checked.value;

    if (!checked.value && animation.value) {
        const rect = button.value.getBoundingClientRect();
        const x = rect.left + button.value.offsetWidth / 2;
        const y = rect.top + button.value.offsetHeight / 2;
        spawnPopover(Ripple, { x, y, particle: false }, "end");
    }
};

defineOptions({
    inheritAttrs: false,
});
</script>

<style lang="scss" scoped>
.ziffeoms {
    position: relative;
    display: inline-block;
    transition: all 0.2s ease;

    > * {
        user-select: none;
    }

    > input {
        position: absolute;
        width: 0;
        height: 0;
        opacity: 0;
        margin: 0;
    }

    > .button {
        position: relative;
        display: inline-flex;
        margin: 0;
        box-sizing: border-box;
        width: 23px;
        height: 23px;
        outline: none;
        background: var(--panel);
        border: 2px solid
            color-mix(in srgb, var(--inputBorderHover) 75%, var(--fg));
        border-radius: 4px;
        cursor: pointer;
        transition: inherit;

        > .check {
            margin: auto;
            opacity: 0;
            color: var(--fgOnAccent);
            font-size: 13px;
            transition: all 0.2s ease;
            -webkit-text-stroke: 1px currentColor;
            transform: translateY(-2px) scale(0.65) translateZ(0.01px);
            text-rendering: geometricprecision;
        }
    }

    &:hover,
    &:has(:focus-visible) {
        > input:not(:disabled) + .button {
            border-color: var(--accent);
        }
    }

    > input:disabled ~ * {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &.checked {
        > .button {
            background-color: var(--accent);
            border-color: var(--accent);

            > .check {
                opacity: 1;
                transform: translateZ(0.01px);
            }
        }
    }
}
</style>
